/**
 * Configuration d'un intercepteur Axios pour gérer l'authentification avec token.
 *
 * Fonctionnalités :
 * - Ajoute automatiquement le token d'accès aux requêtes si disponible.
 * - Gère l'expiration du token et son rafraîchissement via le refresh token.
 * - Empêche les multiples tentatives simultanées de rafraîchissement.
 * - Notifie toutes les requêtes en attente lorsque le token est rafraîchi.
 *
 * Utilise :
 * - tokenStorage : référence contenant le token d'accès et le refresh token.
 * - useAuthStore : store pour gérer l'état de l'authentification.
 * - useRoute : pour récupérer la route courante si nécessaire.
 */
import axios from 'axios';
import { token as tokenStorage } from '@/refs/common.refs';
import { VITE_API_URL } from '@/utils/runtimeConfig';

let isRefreshing = false;
let subscribers: Array<(token: string) => void> = [];

/**
 * Notifie tous les abonnés lorsque le token a été rafraîchi
 * @param token - Nouveau token d'accès
 */
function onRefreshed(token: string) {
  subscribers.forEach((cb) => cb(token));
  subscribers = [];
}

/**
 * Ajoute une requête en attente au tableau de callbacks
 * @param callback - Fonction à appeler une fois que le token est rafraîchi
 */
function addSubscriber(callback: (token: string) => void) {
  subscribers.push(callback);
}

const instance = axios.create({
  baseURL: VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-client-app': 'admin-back-office',
  },
});

/**
 * Intercepteur de requêtes : ajoute le token si disponible
 */
instance.interceptors.request.use((config) => {
  const storedToken = tokenStorage.value;
  if (storedToken?.accessToken) {
    config.headers.Authorization = `Bearer ${storedToken.accessToken}`;
  }
  const lang = localStorage.getItem('locale') ?? 'fr';
  config.headers['accept-language'] = lang;
  // 'both' renvoie les deux variantes (light + dark) en une seule requête —
  // utile pour logoThemes sur le détail commerce, sans dépendre du thème admin.
  config.headers['x-app-theme'] = 'both';
  return config;
});

/**
 * Intercepteur de réponses : gère l’expiration du token et le rafraîchissement
 */
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const isTokenExpired =
      error.response?.status === 401 &&
      error.response?.data?.message?.[0] === 'BEARER_TOKEN_EXPIRED';

    if (!isTokenExpired) {
      return Promise.reject(error);
    }

    if (isTokenExpired && !originalRequest._retry) {
      originalRequest._retry = true;

      const storedToken = tokenStorage.value;
      const refreshToken = storedToken?.refreshToken;

      if (!refreshToken) {
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const { data } = await axios.get(`${VITE_API_URL}auth/refresh`, {
            headers: {
              Authorization: `Bearer ${refreshToken}`,
            },
          });

          const accessToken = data?.data?.token?.accessToken;
          const refreshTokenRefresh = data?.data?.token?.refreshToken;
          tokenStorage.value = {
            accessToken: accessToken,
            refreshToken: refreshTokenRefresh,
          };

          onRefreshed(accessToken);
        } catch (refreshError) {
          tokenStorage.value = null;
          isRefreshing = false;
          return Promise.reject(refreshError);
        }

        isRefreshing = false;
      }

      return new Promise((resolve) => {
        addSubscriber((newAccessToken: string) => {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          resolve(instance(originalRequest));
        });
      });
    }

    return Promise.reject(error);
  },
);

export default instance;
